<template>
  <div class="employee-select">
    <v-card v-if="selectedItem" border color="surface">
      <v-card-text class="d-flex align-center justify-space-between pa-2">
        <div class="d-flex align-center gap-2">
          <v-avatar :color="getAvatarColor(selectedItem.name)" size="35">
            <span>{{ getInitials(selectedItem.name) }}</span>
          </v-avatar>

          <div>
            <div class="font-weight-bold font-size-14">
              {{ selectedItem.name }}
            </div>
            <div class="text-disabled">
              {{ selectedItem.post || 'Посада відсутня' }}
            </div>
          </div>
        </div>
        <v-btn
          color="grey"
          icon="mdi-close"
          size="small"
          variant="text"
          @click="clearSelection" />
      </v-card-text>
    </v-card>
    <v-menu
      v-else
      v-model="menu"
      :close-on-content-click="false"
      location="bottom"
      offset="6">
      <template #activator="{ props }">
        <v-hover>
          <template #default="{ isHovering, props: hoverProps }">
            <div v-bind="hoverProps">
              <div
                :class="{ 'has-error': errorMessages?.length }"
                class="employee-trigger"
                tabindex="0"
                v-bind="props"
                @blur="onBlur">
                <v-icon :color="isHovering ? 'primary' : 'grey'" size="18">
                  mdi-account-outline
                </v-icon>

                <span v-if="selectedItem" class="text-primary">
                  {{ selectedItem.name }}
                </span>

                <span
                  v-else
                  :class="isHovering ? 'text-primary' : 'text-grey'"
                  class="placeholder">
                  Обрати співробітника зі списку...
                </span>
              </div>
            </div>
          </template>
        </v-hover>
      </template>

      <v-card class="employee-menu">
        <div class="employee-search">
          <v-text-field
            v-model="search"
            density="compact"
            hide-details
            placeholder="Пошук за ПІБ..."
            prepend-inner-icon="mdi-magnify"
            variant="plain" />
        </div>

        <v-list
          class="bg-white scrollable-list"
          color="secondary"
          density="compact">
          <v-list-item
            v-for="item in filteredItems"
            :key="item.id"
            class="employee-item"
            color="blue-grey-lighten-5"
            @click="selectItem(item)">
            <template #prepend>
              <v-avatar :color="getAvatarColor(item.name)" size="32">
                {{ getInitials(item.name) }}
              </v-avatar>
            </template>

            <v-list-item-title class="employee-item__title">
              {{ item.name }}
            </v-list-item-title>

            <v-list-item-subtitle class="employee-item__subtitle">
              {{ item.post || 'Посада відсутня' }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item v-if="!filteredItems.length">
            <v-list-item-title class="text-grey">
              Нічого не знайдено
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <div v-if="!hideDetails" style="height: 22px">
      <div v-if="errorMessages?.length" class="employee-errors">
        <div v-for="(err, i) in errorMessages" :key="i">
          {{ err }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from '@/plugins'
import { fillDirectory, getDirectory } from '@/directory'
import { getAvatarColor, getInitials } from './helpers'

export default {
  name: 'EmployeeAutocomplete',
  props: {
    modelValue: [Number, String, null],
    defaultItem: Object,
    errorMessages: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
    },
  },
  emits: ['update:modelValue', 'blur'],
  methods: { getAvatarColor, getInitials },
  async setup(props, { emit }) {
    const store = useStore()
    const currentUser = computed(() => store.state.user.userData)
    const internalDefaultUsed = ref(false)

    const menu = ref(false)
    const search = ref('')

    await fillDirectory('userList')

    const allUsers = computed(() => {
      const list = getDirectory('userList')
      return list.filter(u => u.id !== currentUser.value.id)
    })

    const selectedItem = computed({
      get() {
        if (
          !internalDefaultUsed.value &&
          props.defaultItem &&
          !props.modelValue
        ) {
          return props.defaultItem
        }
        return allUsers.value.find(u => u.id === props.modelValue) || null
      },
      set(item) {
        internalDefaultUsed.value = true
        emit('update:modelValue', item?.id || null)
      },
    })

    const filteredItems = computed(() => {
      if (!search.value) return allUsers.value
      return allUsers.value.filter(i =>
        i.name.toLowerCase().includes(search.value.toLowerCase())
      )
    })

    const selectItem = item => {
      selectedItem.value = item
      menu.value = false
      search.value = ''
    }

    const clearSelection = () => {
      internalDefaultUsed.value = true
      selectedItem.value = null
    }

    const onBlur = () => {
      emit('blur')
    }

    return {
      menu,
      search,
      selectedItem,
      filteredItems,
      selectItem,
      clearSelection,
      onBlur,
    }
  },
}
</script>

<style lang="scss" scoped>
.employee-select {
  width: 100%;
}

.employee-trigger {
  width: 100%;
  padding: 10px 14px;
  background: rgb(255, 255, 255);
  border: 1.5px dashed rgb(var(--v-theme-surface-border));
  border-radius: 10px;
  cursor: pointer;
  color: rgb(var(--v-theme-grey));
  text-align: left;
  display: flex;
  font-size: 13px;
  align-items: center;
  gap: 8px;
  transition: 0.15s;

  &:hover {
    border: 1.5px dashed rgb(var(--v-theme-secondary));
  }

  &.has-error {
    border-color: rgb(var(--v-theme-error));
  }

  .placeholder {
    color: rgb(var(--v-theme-grey));
  }
}

.employee-menu {
  min-width: 100%;
  border-radius: 14px !important;
  padding: 6px;
  animation: ntmDdIn 0.12s ease !important;
  border: 1.5px solid rgb(var(--v-theme-surface-border)) !important;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14) !important;

  :deep(.v-list) {
    max-height: 200px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgb(var(--v-theme-darkGrey));
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: rgb(var(--v-theme-grey));
    }
  }
}

.scrollable-list {
  max-height: 195px;
  overflow-y: auto !important;
}

.employee-search {
  padding: 0 8px 3px;
  border-bottom: 1px solid rgb(var(--v-theme-surface-border));
}

.employee-item {
  border-radius: 10px;
  padding: 6px 8px;

  &__title {
    font-size: 13px;
    font-weight: 600;
  }

  &__subtitle {
    font-size: 12px;
    color: rgb(var(--v-theme-grey));
  }

  &:hover {
    background: rgb(var(--v-theme-surface)) !important;
  }
}

.employee-errors {
  margin-top: 4px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}
</style>
